import { useEffect, useRef, useState, type RefObject } from "react";
import { parseAdminCharacterNumbers, validateAdminCharacterStep, type AdminCharacterTextDraft } from "./admin-character-draft";
import { SettingsRequestError, settingsApi } from "./api";
import { CHARACTER_NAME_MAX_LENGTH } from "../../character-catalog/character-names";
import { revokeImageDraft, validateCharacterImageFile, type CharacterImageDraft } from "./character-image-upload";
import { createRequestGenerationGate } from "./request-generation";
import { CreatorTransferDialog } from "./CreatorTransferDialog";
import { CharacterImageUploadField } from "./CharacterImageUploadField";
import { Skeleton, SkeletonGrid, SkeletonLines } from "./Skeleton";
import type { AdminCharacterBinding, AdminCharacterImage, AdminCharacterProfile, AdminCharacterSdPrompts, CharacterManagementItem, CharacterCatalogTab, CharacterImageKind, CharacterSettingsMode, GuildMemberOption, UserCharacterSummary } from "./types";
import "./image-upload.css";
import "./admin-character-lifecycle.css";

const CREATE_STEPS = ["人设", "正向题词", "立绘", "封图", "化身"] as const;
const EMPTY_CHARACTER_DRAFT: AdminCharacterTextDraft = { name: "", age: "", heightCm: "", profileText: "", positivePrompt: "" };

function characterImageError(cause: unknown, kind: CharacterImageKind): string {
  const code = cause instanceof Error ? cause.message : "";
  return code === "character_image_too_large" ? "图片不能超过 10 MiB。"
    : code === "character_image_type_invalid" ? "只支持 PNG、JPEG 或 WebP 图片。"
      : code === "character_image_ratio_invalid" ? (kind === "portrait" ? "立绘必须为 9:21 比例。" : "封图必须为 1:1 比例。")
        : "无法读取这张图片，请选择有效的 PNG、JPEG 或 WebP 文件。";
}

function characterCreateError(cause: unknown): string {
  if (cause instanceof SettingsRequestError) {
    if (cause.status === 401) return "登录状态已失效。所有草稿仍保留在当前页面；请不要刷新。";
    if (cause.status === 403) return "当前账号没有创建角色权限。所有草稿仍保留在当前页面；请不要刷新。";
    if (cause.code === "character_name_conflict") return "这个角色名已被使用。所有草稿仍保留，请返回第一步修改名称。";
    if (cause.code === "character_request_conflict") return "服务器已记录同一请求标识但内容不同。为防止重复创建，本次未重提；草稿仍保留，请先核对角色目录。";
    if (cause.code === "character_image_storage_unavailable") return "图片存储暂时不可用，角色尚未创建。所有草稿仍保留，可使用同一请求手动重试。";
    if (cause.code === "character_image_ratio_invalid") return "服务端未接受图片比例。所有草稿仍保留，请返回图片步骤重新裁剪。";
    if (cause.status === 413 || cause.code === "character_create_too_large" || cause.code === "character_image_too_large") return "创建数据或图片过大。所有草稿仍保留，请压缩图片后再试。";
  }
  return "角色创建请求未完成。所有草稿仍保留在当前页面；请不要刷新，可使用同一请求手动重试。";
}

function fileDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("character_draft_export_failed")), { once: true });
    reader.addEventListener("error", () => reject(new Error("character_draft_export_failed")), { once: true });
    reader.readAsDataURL(file);
  });
}

function TrashIcon() {
  return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M10 11v6m4-6v6M9 7l1-2h4l1 2m-9 0 1 13h10l1-13" /></svg>;
}

function DialogFrame({ labelledBy, className, busy = false, initialFocus, onClose, children }: {
  labelledBy: string;
  className: string;
  busy?: boolean;
  initialFocus?: RefObject<HTMLElement | null> | undefined;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const restoreTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (initialFocus?.current ?? dialogRef.current?.querySelector<HTMLElement>("button:not(:disabled), textarea:not(:disabled), input:not(:disabled)"))?.focus();
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape" && !busy) { event.preventDefault(); onCloseRef.current(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>("button:not(:disabled), textarea:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex='-1'])")];
      if (focusable.length === 0) { event.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => { document.removeEventListener("keydown", handleKeyDown); restoreTarget?.focus(); };
  }, [busy, initialFocus]);
  return <div className={`${className}-backdrop`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section ref={dialogRef} className={`${className}-dialog`} role="dialog" aria-modal="true" aria-labelledby={labelledBy}>{children}</section>
  </div>;
}

export function AdminCharacterSettings({ items, isAdmin = true, actorUid = "", mode, onCatalogChanged, onCharacterCreated, onProfileSaved, onSdPromptsSaved, onImageSaved, onBindingChanged, onVoiceSource, onSharedVoiceSource, onImagePrompts }: {
  items?: CharacterManagementItem[] | undefined;
  isAdmin?: boolean | undefined;
  actorUid?: string | undefined;
  mode?: CharacterSettingsMode | undefined;
  onCatalogChanged?: (() => void) | undefined;
  onCharacterCreated?: ((character: CharacterManagementItem) => void) | undefined;
  onProfileSaved?: ((profile: AdminCharacterProfile) => void) | undefined;
  onSdPromptsSaved?: ((prompts: AdminCharacterSdPrompts) => void) | undefined;
  onImageSaved?: ((image: AdminCharacterImage) => void) | undefined;
  onBindingChanged?: (() => void) | undefined;
  onVoiceSource?: ((character: UserCharacterSummary) => void) | undefined;
  onSharedVoiceSource?: ((character: UserCharacterSummary) => void) | undefined;
  onImagePrompts?: ((character: UserCharacterSummary) => void) | undefined;
  onInteractions?: (() => void) | undefined;
}) {
  const mineMode = mode === "mine";
  const personalMode = mode === "personal";
  const forceSharedMode = mineMode || mode === "admin";
  const [characters, setCharacters] = useState<AdminCharacterBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyCharacterId, setBusyCharacterId] = useState<string | null>(null);
  const [imageTarget, setImageTarget] = useState<{ character: AdminCharacterBinding; kind: CharacterImageKind } | null>(null);
  const [adminImage, setAdminImage] = useState<AdminCharacterImage | null>(null);
  const [imageDraft, setImageDraft] = useState<CharacterImageDraft | null>(null);
  const imageDraftRef = useRef<CharacterImageDraft | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageValidating, setImageValidating] = useState(false);
  const [imageSaving, setImageSaving] = useState(false);
  const [imageVisualLoading, setImageVisualLoading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [imageConflict, setImageConflict] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const imageRequestGeneration = useRef(0);
  const imageValidationGeneration = useRef(0);
  const [bindingTarget, setBindingTarget] = useState<AdminCharacterBinding | null>(null);
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState<GuildMemberOption[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const requestGeneration = useRef(0);
  const [profileTarget, setProfileTarget] = useState<AdminCharacterBinding | null>(null);
  const [profile, setProfile] = useState<AdminCharacterProfile | null>(null);
  const [profileContent, setProfileContent] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState("");
  const profileRef = useRef<HTMLTextAreaElement>(null);
  const profileRequests = useRef(createRequestGenerationGate());
  const [sdTarget, setSdTarget] = useState<AdminCharacterBinding | null>(null);
  const [sdPrompts, setSdPrompts] = useState<AdminCharacterSdPrompts | null>(null);
  const [positivePrompt, setPositivePrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [sdLoading, setSdLoading] = useState(false);
  const [sdSaving, setSdSaving] = useState(false);
  const [sdError, setSdError] = useState("");
  const positiveRef = useRef<HTMLTextAreaElement>(null);
  const sdRequests = useRef(createRequestGenerationGate());
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [createDraft, setCreateDraft] = useState<AdminCharacterTextDraft>(EMPTY_CHARACTER_DRAFT);
  const [createOwner, setCreateOwner] = useState<GuildMemberOption | null>(null);
  const [createPortrait, setCreatePortrait] = useState<CharacterImageDraft | null>(null);
  const createPortraitRef = useRef<CharacterImageDraft | null>(null);
  const [createCover, setCreateCover] = useState<CharacterImageDraft | null>(null);
  const createCoverRef = useRef<CharacterImageDraft | null>(null);
  const [createImageValidating, setCreateImageValidating] = useState(false);
  const createNameRef = useRef<HTMLInputElement>(null);
  const createPromptRef = useRef<HTMLTextAreaElement>(null);
  const [createError, setCreateError] = useState("");
  const [createSaving, setCreateSaving] = useState(false);
  const createSavingRef = useRef(false);
  const [createExporting, setCreateExporting] = useState(false);
  const [createExportNotice, setCreateExportNotice] = useState("");
  const [createRequestId, setCreateRequestId] = useState("");
  const [notice, setNotice] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminCharacterBinding | null>(null);
  const [deleteRequestId, setDeleteRequestId] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [catalogTab, setCatalogTab] = useState<CharacterCatalogTab>("all");
  const [characterSearch, setCharacterSearch] = useState("");
  const [sharedEditOn, setSharedEditOn] = useState(false);
  const [creatorTarget, setCreatorTarget] = useState<CharacterManagementItem | null>(null);

  const externalItems = items ?? null;
  const allCharacters = externalItems ?? characters.map((character) => ({
    ...character,
    kind: "system" as const,
    is_mine: false,
    is_avatar: character.owner_uid !== null,
    can_manage: true,
    creator_uid: null,
    creator_name: null,
    creator_revision: 0,
    usage_count: 0,
  }));
  const sharedEditEligible = isAdmin || allCharacters.some((character) => character.kind === "player" && character.can_manage);
  const sharedEditorOpen = Boolean(imageTarget || bindingTarget || profileTarget || sdTarget || deleteTarget || creatorTarget);
  const normalizedSearch = characterSearch.trim().toLocaleLowerCase();
  const activeCatalogTab = mineMode ? "mine" : catalogTab;
  const sharedEditActive = forceSharedMode || sharedEditOn;
  const visibleCharacters = allCharacters.filter((character) => (activeCatalogTab === "all"
    || (activeCatalogTab === "player" && (character.kind === "player" || character.is_avatar))
    || (activeCatalogTab === "system" && character.kind === "system")
    || (activeCatalogTab === "mine" && character.is_mine))
    && (!normalizedSearch || character.name.toLocaleLowerCase().includes(normalizedSearch)));
  const hasCreateDraft = Boolean(createDraft.name || createDraft.age || createDraft.heightCm || createDraft.profileText || createDraft.positivePrompt || createOwner || createPortrait || createCover);

  useEffect(() => {
    if (externalItems) { setLoading(false); return; }
    void settingsApi.loadAdminCharacters().then(setCharacters).catch(() => setError("无法读取化身角色列表。"))
      .finally(() => setLoading(false));
  }, [externalItems]);

  useEffect(() => () => {
    imageRequestGeneration.current += 1;
    imageValidationGeneration.current += 1;
    revokeImageDraft(imageDraftRef.current);
    revokeImageDraft(createPortraitRef.current);
    revokeImageDraft(createCoverRef.current);
  }, []);

  useEffect(() => {
    if (!hasCreateDraft) return;
    function warnBeforeUnload(event: BeforeUnloadEvent): void {
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [hasCreateDraft]);

  async function refreshCharacters(): Promise<void> {
    setError("");
    try {
      if (externalItems) return;
      setCharacters(await settingsApi.loadAdminCharacters());
    } catch {
      setError("角色列表刷新失败，请稍后重试。");
      throw new Error("admin_characters_refresh_failed");
    }
  }

  function replaceCreateImageDraft(kind: CharacterImageKind, next: CharacterImageDraft | null): void {
    const currentRef = kind === "portrait" ? createPortraitRef : createCoverRef;
    if (currentRef.current !== next) revokeImageDraft(currentRef.current);
    currentRef.current = next;
    if (kind === "portrait") setCreatePortrait(next);
    else setCreateCover(next);
  }

  function openCreateWizard(): void {
    if (!createRequestId) setCreateRequestId(crypto.randomUUID());
    setCreateError("");
    setCreateExportNotice("");
    setNotice("");
    setCreateOpen(true);
    if (isAdmin && createStep === 5 && members.length === 0) loadMembers("");
  }

  function closeCreateWizard(): void {
    if (createSaving) return;
    setCreateImageValidating(false);
    setCreateOpen(false);
    setCreateError("");
    setCreateExportNotice("");
  }

  function updateCreateDraft(patch: Partial<AdminCharacterTextDraft>): void {
    setCreateDraft((current) => ({ ...current, ...patch }));
    setCreateError("");
  }

  function nextCreateStep(): void {
    const stepError = validateAdminCharacterStep(createStep, createDraft, Boolean(createPortrait), Boolean(createCover));
    if (stepError) { setCreateError(stepError); return; }
    const nextStep = Math.min(5, createStep + 1);
    setCreateError("");
    setCreateStep(nextStep);
    if (isAdmin && nextStep === 5) {
      setQuery(""); setMembers([]); setNextCursor(null); setMembersError("");
      loadMembers("");
    }
  }

  function previousCreateStep(): void {
    if (createSaving) return;
    setCreateError("");
    setCreateStep((current) => Math.max(1, current - 1));
  }

  function resetCreateWizard(): void {
    replaceCreateImageDraft("portrait", null);
    replaceCreateImageDraft("cover", null);
    setCreateDraft(EMPTY_CHARACTER_DRAFT);
    setCreateOwner(null);
    setCreateStep(1);
    setCreateRequestId("");
    setCreateError("");
    setCreateOpen(false);
  }

  async function createCharacter(): Promise<void> {
    if (createSavingRef.current || createSaving) return;
    for (let step = 1; step <= 4; step += 1) {
      const stepError = validateAdminCharacterStep(step, createDraft, Boolean(createPortrait), Boolean(createCover));
      if (stepError) { setCreateStep(step); setCreateError(stepError); return; }
    }
    if (!createPortrait || !createCover) return;
    const requestId = createRequestId || crypto.randomUUID();
    if (!createRequestId) setCreateRequestId(requestId);
    const numbers = parseAdminCharacterNumbers(createDraft);
    createSavingRef.current = true;
    setCreateSaving(true); setCreateError("");
    try {
      const created = await (isAdmin ? settingsApi.createAdminCharacter : settingsApi.createUserCharacter)({
        request_id: requestId,
        name: createDraft.name.trim(),
        age: numbers.age,
        height_cm: numbers.height_cm,
        profile_text: createDraft.profileText,
        positive_prompt: createDraft.positivePrompt,
        owner_uid: createOwner?.uid ?? null,
        portrait: createPortrait.file,
        cover: createCover.file,
      });
      const createdItem: CharacterManagementItem = {
        ...created,
        kind: isAdmin ? "system" : "player",
        is_mine: !isAdmin || created.owner_uid === actorUid,
        is_avatar: created.owner_uid !== null,
        can_manage: true,
        creator_uid: isAdmin ? null : actorUid,
        creator_name: !isAdmin && created.owner_uid === actorUid ? created.owner_name : null,
        creator_revision: 0,
        usage_count: 0,
      };
      setCharacters((current) => [...current.filter((item) => item.id !== created.id), created]);
      onCharacterCreated?.(createdItem);
      setCatalogTab("all");
      setCharacterSearch("");
      resetCreateWizard();
      setNotice(`已创建角色 ${created.name}。`);
      onBindingChanged?.();
      onCatalogChanged?.();
      void refreshCharacters().catch(() => undefined);
    } catch (cause) {
      setCreateError(characterCreateError(cause));
    } finally {
      createSavingRef.current = false;
      setCreateSaving(false);
    }
  }

  async function exportCreateDraft(): Promise<void> {
    if (createExporting || createSaving) return;
    setCreateExporting(true);
    try {
      const image = async (draft: CharacterImageDraft | null) => draft ? {
        name: draft.file.name,
        type: draft.file.type,
        width: draft.width,
        height: draft.height,
        data_url: await fileDataUrl(draft.file),
      } : null;
      const payload = {
        schema_version: "discord-character-create-draft/v1",
        exported_at: new Date().toISOString(),
        request_id: createRequestId,
        name: createDraft.name,
        age: createDraft.age,
        height_cm: createDraft.heightCm,
        profile_text: createDraft.profileText,
        positive_prompt: createDraft.positivePrompt,
        owner_uid: createOwner?.uid ?? null,
        portrait: await image(createPortrait),
        cover: await image(createCover),
      };
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const safeName = createDraft.name.trim().replace(/[^\p{L}\p{N}_-]+/gu, "-").replace(/^-+|-+$/g, "") || "character";
      anchor.href = url;
      anchor.download = `${safeName}-draft.json`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setCreateExportNotice("角色草稿已导出到本机；导出不会创建或重试角色。");
    } catch {
      setCreateError("草稿导出失败；当前页面内的文字和图片仍然保留，请不要刷新。");
    } finally {
      setCreateExporting(false);
    }
  }

  function openDeleteDialog(character: AdminCharacterBinding): void {
    if (character.id === "catgirl") return;
    setDeleteTarget(character);
    setDeleteRequestId(crypto.randomUUID());
    setDeleteError("");
    setNotice("");
  }

  async function deleteCharacter(): Promise<void> {
    if (!deleteTarget || deleteTarget.id === "catgirl" || deleteSaving) return;
    setDeleteSaving(true); setDeleteError("");
    try {
      const result = await (isAdmin ? settingsApi.deleteAdminCharacter : settingsApi.deleteUserCharacter)(deleteTarget.id, deleteRequestId, deleteTarget.binding_version);
      if (result.id !== deleteTarget.id) throw new Error("admin_character_delete_mismatch");
      const deletedName = deleteTarget.name;
      setCharacters((current) => current.filter((item) => item.id !== result.id));
      setDeleteTarget(null); setDeleteRequestId("");
      setNotice(`已删除角色 ${deletedName}。`);
      onBindingChanged?.();
      onCatalogChanged?.();
      void refreshCharacters().catch(() => undefined);
    } catch (cause) {
      setDeleteError(cause instanceof SettingsRequestError && cause.status === 409
        ? "角色绑定版本已变化，请重新加载角色列表后再试。"
        : "角色删除失败，确认信息已保留，请稍后重试。");
    } finally {
      setDeleteSaving(false);
    }
  }

  async function reloadDeleteConflict(): Promise<void> {
    try {
      await refreshCharacters();
      setDeleteTarget(null); setDeleteRequestId(""); setDeleteError("");
    } catch { /* 列表错误已经展示在页面。 */ }
  }

  function replaceImageDraft(next: CharacterImageDraft | null): void {
    if (imageDraftRef.current !== next) revokeImageDraft(imageDraftRef.current);
    imageDraftRef.current = next;
    setImageDraft(next);
  }

  function requestImageMetadata(target: { character: AdminCharacterBinding; kind: CharacterImageKind }): void {
    const generation = ++imageRequestGeneration.current;
    setImageLoading(true); setImageError(""); setImageConflict(false);
    void (isAdmin ? settingsApi.loadAdminCharacterImage : settingsApi.loadUserCharacterImage)(target.character.id, target.kind).then((value) => {
      if (generation !== imageRequestGeneration.current) return;
      if (value.id !== target.character.id || value.kind !== target.kind) throw new Error("admin_character_image_mismatch");
      setAdminImage(value); setImageVisualLoading(imageDraftRef.current === null);
    }).catch(() => {
      if (generation === imageRequestGeneration.current) setImageError("无法读取当前角色图片，请稍后重试。");
    }).finally(() => {
      if (generation === imageRequestGeneration.current) setImageLoading(false);
    });
  }

  function openImageEditor(character: AdminCharacterBinding, kind: CharacterImageKind): void {
    const target = { character, kind };
    replaceImageDraft(null); setAdminImage(null); setImageTarget(target); setImageError(""); setImageConflict(false); setImageValidating(false);
    requestImageMetadata(target);
  }

  function closeImageEditor(): void {
    if (imageSaving) return;
    imageRequestGeneration.current += 1;
    imageValidationGeneration.current += 1;
    replaceImageDraft(null); setImageTarget(null); setAdminImage(null); setImageError(""); setImageConflict(false); setImageValidating(false);
  }

  async function chooseImageFile(file: File): Promise<void> {
    if (!imageTarget || imageSaving) return;
    const generation = ++imageValidationGeneration.current;
    setImageValidating(true); setImageError(""); setImageConflict(false);
    try {
      const dimensions = await validateCharacterImageFile(file, imageTarget.kind);
      if (generation !== imageValidationGeneration.current) return;
      const next: CharacterImageDraft = { file, previewUrl: URL.createObjectURL(file), ...dimensions };
      replaceImageDraft(next); setImageVisualLoading(true);
    } catch (cause) {
      if (generation !== imageValidationGeneration.current) return;
      setImageError(characterImageError(cause, imageTarget.kind));
    } finally {
      if (generation === imageValidationGeneration.current) setImageValidating(false);
    }
  }

  async function saveImage(): Promise<void> {
    const draftFile = imageDraft?.file;
    if (!imageTarget || !adminImage || !draftFile || imageSaving || imageValidating) return;
    setImageSaving(true); setImageError(""); setImageConflict(false);
    try {
      const updated = await (isAdmin ? settingsApi.updateAdminCharacterImage : settingsApi.updateUserCharacterImage)(imageTarget.character.id, imageTarget.kind, draftFile, adminImage.revision);
      if (updated.id !== imageTarget.character.id || updated.kind !== imageTarget.kind) throw new Error("admin_character_image_mismatch");
      onImageSaved?.(updated);
      replaceImageDraft(null); setImageTarget(null); setAdminImage(null);
    } catch (cause) {
      const conflict = cause instanceof SettingsRequestError
        && (cause.status === 409 || cause.code === "character_image_revision_conflict" || cause.code === "discord_activity_character_image_revision_conflict");
      setImageConflict(conflict);
      setImageError(conflict
        ? "图片已在别处更新。草稿已保留，请刷新图片修订后再保存。"
        : "图片保存失败。草稿已保留，请稍后重试。");
    } finally {
      setImageSaving(false);
    }
  }

  function loadMembers(search: string, cursor?: string, append = false): void {
    const generation = ++requestGeneration.current;
    setMembersLoading(true);
    setMembersError("");
    void settingsApi.loadGuildMembers(search, cursor).then((page) => {
      if (generation !== requestGeneration.current) return;
      setMembers((current) => append ? [...current, ...page.members] : page.members);
      setNextCursor(page.next_cursor);
    }).catch(() => {
      if (generation !== requestGeneration.current) return;
      setMembersError("无法读取服务器成员，请稍后重试。");
      if (!append) setMembers([]);
    }).finally(() => { if (generation === requestGeneration.current) setMembersLoading(false); });
  }

  function openBinding(character: CharacterManagementItem): void {
    if (!isAdmin) {
      void updateOwnBinding(character);
      return;
    }
    setBindingTarget(character);
    setQuery(""); setMembers([]); setNextCursor(null); setMembersError("");
    loadMembers("");
  }

  async function updateOwnBinding(character: CharacterManagementItem): Promise<void> {
    if (busyCharacterId !== null || !actorUid) return;
    setBusyCharacterId(character.id);
    setError("");
    try {
      await settingsApi.updateUserCharacterBinding(character.id, character.is_avatar ? null : actorUid, character.binding_version);
      onBindingChanged?.();
      onCatalogChanged?.();
    } catch (cause) {
      setError(cause instanceof SettingsRequestError && cause.status === 409
        ? "化身绑定已发生变化，请刷新角色列表后重试。"
        : "无法更新我的化身绑定，请稍后重试。");
    } finally { setBusyCharacterId(null); }
  }

  async function updateBinding(character: AdminCharacterBinding, ownerUid: string | null): Promise<void> {
    if (busyCharacterId !== null) return;
    setBusyCharacterId(character.id); setMembersError("");
    try {
      const updated = await settingsApi.updateAdminCharacterBinding(character.id, ownerUid, character.binding_version);
      setCharacters((current) => current.map((item) => item.id === updated.id ? updated : item));
      setBindingTarget(null);
      onBindingChanged?.();
      onCatalogChanged?.();
    } catch { setMembersError("化身绑定失败，数据可能已变化，请刷新后重试。"); }
    finally { setBusyCharacterId(null); }
  }

  function openProfile(character: AdminCharacterBinding): void {
    const generation = profileRequests.current.begin();
    setProfileTarget(character); setProfile(null); setProfileContent(""); setProfileError(""); setProfileLoading(true);
    void (isAdmin ? settingsApi.loadAdminCharacterProfile : settingsApi.loadUserCharacterProfile)(character.id).then((value) => {
      if (!profileRequests.current.isCurrent(generation)) return;
      if (value.id !== character.id) throw new Error("admin_character_profile_mismatch");
      setProfile(value); setProfileContent(value.content);
    }).catch(() => { if (profileRequests.current.isCurrent(generation)) setProfileError("无法读取当前生效人设，请稍后重试。"); })
      .finally(() => { if (profileRequests.current.isCurrent(generation)) setProfileLoading(false); });
  }

  function closeProfile(): void {
    if (profileSaving) return;
    profileRequests.current.invalidate();
    setProfileTarget(null); setProfile(null); setProfileContent(""); setProfileError("");
  }

  async function saveProfile(): Promise<void> {
    if (!profile || !profileTarget || profile.id !== profileTarget.id || profileSaving || profileContent === profile.content || profileContent.trim().length === 0 || profileContent.length > 20_000) return;
    setProfileSaving(true); setProfileError("");
    try {
      const updated = await (isAdmin ? settingsApi.updateAdminCharacterProfile : settingsApi.updateUserCharacterProfile)(profile.id, profileContent, profile.profile_revision);
      if (updated.id !== profile.id || profile.id !== profileTarget.id) throw new Error("admin_character_profile_mismatch");
      profileRequests.current.invalidate();
      onProfileSaved?.(updated); setProfileTarget(null); setProfile(null); setProfileContent("");
    } catch { setProfileError("人设保存失败，数据可能已变化。草稿已保留，请刷新后核对再试。"); }
    finally { setProfileSaving(false); }
  }

  function openSdPrompts(character: AdminCharacterBinding): void {
    const generation = sdRequests.current.begin();
    setSdTarget(character); setSdPrompts(null); setPositivePrompt(""); setNegativePrompt(""); setSdError(""); setSdLoading(true);
    void (isAdmin ? settingsApi.loadAdminCharacterSdPrompts : settingsApi.loadUserCharacterSdPrompts)(character.id).then((value) => {
      if (!sdRequests.current.isCurrent(generation)) return;
      if (value.id !== character.id) throw new Error("admin_character_sd_prompts_mismatch");
      setSdPrompts(value); setPositivePrompt(value.positive_prompt); setNegativePrompt(value.negative_prompt);
    }).catch(() => { if (sdRequests.current.isCurrent(generation)) setSdError("无法读取当前生效题词，请稍后重试。"); })
      .finally(() => { if (sdRequests.current.isCurrent(generation)) setSdLoading(false); });
  }

  function closeSdPrompts(): void {
    if (sdSaving) return;
    sdRequests.current.invalidate();
    setSdTarget(null); setSdPrompts(null); setPositivePrompt(""); setNegativePrompt(""); setSdError("");
  }

  async function saveSdPrompts(): Promise<void> {
    const dirty = sdPrompts && (positivePrompt !== sdPrompts.positive_prompt || negativePrompt !== sdPrompts.negative_prompt);
    if (!sdPrompts || !sdTarget || sdPrompts.id !== sdTarget.id || !dirty || sdSaving || positivePrompt.trim().length === 0 || positivePrompt.length > 4_000 || negativePrompt.length > 4_000) return;
    setSdSaving(true); setSdError("");
    try {
      const updated = await (isAdmin ? settingsApi.updateAdminCharacterSdPrompts : settingsApi.updateUserCharacterSdPrompts)(sdPrompts.id, positivePrompt, negativePrompt, sdPrompts.sd_revision);
      if (updated.id !== sdPrompts.id || sdPrompts.id !== sdTarget.id) throw new Error("admin_character_sd_prompts_mismatch");
      sdRequests.current.invalidate();
      onSdPromptsSaved?.(updated); setSdTarget(null); setSdPrompts(null);
    } catch { setSdError("题词保存失败，数据可能已变化。草稿已保留，请刷新后核对再试。"); }
    finally { setSdSaving(false); }
  }

  const createImageKind: CharacterImageKind = createStep === 4 ? "cover" : "portrait";
  const activeCreateImage = createImageKind === "portrait" ? createPortrait : createCover;
  const createInitialFocus = createStep === 1 ? createNameRef : createStep === 2 ? createPromptRef : undefined;

  if (loading) return <SkeletonGrid items={5} variant="row" label="正在读取角色管理" />;
  return <div className="admin-character-stack">
    <div className="admin-character-toolbar">
      {mode === undefined || mode === "admin" ? <div className="character-catalog-tabs" role="tablist" aria-label="角色分类">
        {([['all', '全部角色'], ['player', '玩家角色'], ['system', '系统角色'], ['mine', '我的角色']] as Array<[CharacterCatalogTab, string]>).map(([id, label]) => <button role="tab" aria-selected={catalogTab === id} className={catalogTab === id ? "selected" : ""} type="button" key={id} onClick={() => setCatalogTab(id)}>{label}</button>)}
      </div> : null}
      <input className="character-catalog-search" type="search" maxLength={100} value={characterSearch} aria-label="搜索角色" placeholder="搜索角色…" onChange={(event) => setCharacterSearch(event.target.value)} />
      {!personalMode ? <button className="panel-fab shared-write" type="button" aria-label={mineMode ? "新建我的角色" : "新建共享角色"} title={mineMode ? "新建角色（向所有用户可见）" : "新建共享角色（向所有用户可见）"} onClick={openCreateWizard}>+</button> : null}
      {mode === undefined && sharedEditEligible ? <button className={`shared-edit-toggle${sharedEditOn ? " enabled" : ""}`} type="button" role="switch" aria-checked={sharedEditOn} aria-label={sharedEditOn ? "关闭共享编辑" : "开启共享编辑"} title={sharedEditOn ? "关闭共享编辑" : "开启共享编辑"} disabled={sharedEditorOpen} onClick={() => setSharedEditOn((current) => !current)}><span aria-hidden="true">✎</span></button> : null}
    </div>
    {notice ? <p className="action-success" role="status">{notice}</p> : null}
    {error ? <p className="action-error" role="alert">{error}</p> : null}
    {visibleCharacters.length === 0 ? <p className="panel-state">这个分类下还没有角色。</p> : <div className="admin-character-list">
      {visibleCharacters.map((character) => <article className="admin-character-row" key={character.id}>
        <div className="admin-character-summary"><strong>{character.name}</strong><small className="character-usage-count">当前使用：{character.usage_count ?? 0} 人</small>{character.is_avatar ? <small className="avatar-character-note">化身</small> : null}{character.id === "catgirl" ? <small className="default-character-note">默认角色不可删除</small> : null}</div>
        <div className={`admin-character-actions${isAdmin ? " admin-character-actions-admin" : ""}${personalMode ? " user-character-actions" : ""}${sharedEditActive && character.can_manage ? " shared-edit-actions" : ""}`} aria-label={`${character.name}角色操作`}>
          {personalMode ? <><button className="secondary outlined" type="button" title="设置只对我生效的图片题词" onClick={() => onImagePrompts?.(character)}>图片题词</button><button className="secondary outlined" type="button" title="设置只对我生效的声源" onClick={() => onVoiceSource?.(character)}>声源</button></> : <>
          <button className={sharedEditActive && character.can_manage ? "shared-edit-button" : "secondary outlined"} type="button" disabled={!sharedEditActive || !character.can_manage} title={!sharedEditActive ? "开启全局编辑后可修改共享立绘" : !character.can_manage ? "你没有修改此角色共享立绘的权限" : "编辑共享立绘"} onClick={() => openImageEditor(character, "portrait")}>立绘</button>
          <button className={sharedEditActive && character.can_manage ? "shared-edit-button" : "secondary outlined"} type="button" disabled={!sharedEditActive || !character.can_manage} title={!sharedEditActive ? "开启全局编辑后可修改共享封图" : !character.can_manage ? "你没有修改此角色共享封图的权限" : "编辑共享封图"} onClick={() => openImageEditor(character, "cover")}>封图</button>
          <button className={sharedEditActive && character.can_manage ? "shared-edit-button" : "secondary outlined"} type="button" disabled={!sharedEditActive || !character.can_manage} title={!sharedEditActive ? "开启全局编辑后可修改共享人设" : !character.can_manage ? "你没有修改此角色共享人设的权限" : "编辑共享人设"} onClick={() => openProfile(character)}>人设</button>
          <button className={sharedEditActive && character.can_manage ? "shared-edit-button" : "secondary outlined"} type="button" disabled={!sharedEditActive || !character.can_manage} title={!sharedEditActive ? "开启全局编辑后可修改共享题词" : !character.can_manage ? "你没有修改此角色共享题词的权限" : "编辑共享题词"} onClick={() => openSdPrompts(character)}>题词</button>
          <button className={sharedEditActive && character.can_manage ? "shared-edit-button" : "secondary outlined"} type="button" disabled={sharedEditActive && character.can_manage && !onSharedVoiceSource} title={sharedEditActive && character.can_manage ? "编辑共享声源" : "设置只对我生效的声源"} onClick={() => sharedEditActive && character.can_manage ? onSharedVoiceSource?.(character) : onVoiceSource?.(character)}>声源</button>
          <button className={sharedEditActive && character.can_manage ? `shared-edit-button${character.is_avatar ? " active" : ""}` : "secondary outlined"} type="button" disabled={!sharedEditActive || !character.can_manage} aria-pressed={character.is_avatar} title={!sharedEditActive ? "开启全局编辑后可修改化身绑定" : !character.can_manage ? "你没有修改此角色化身绑定的权限" : "编辑共享化身绑定"} onClick={() => openBinding(character)}>化身</button>
          {isAdmin ? <button className={sharedEditActive ? "shared-edit-button" : "secondary outlined"} type="button" disabled={!sharedEditActive} title={!sharedEditActive ? "开启全局编辑后可转让角色作者" : "转让或清除角色作者"} onClick={() => { setNotice(""); setCreatorTarget(character); }}>转让</button> : null}
          <button className={`${sharedEditActive && character.can_manage ? "shared-edit-button" : "secondary outlined"} character-delete-button`} type="button" aria-label={`删除角色 ${character.name}`} title={character.id === "catgirl" ? "默认角色不可删除" : !sharedEditActive ? "开启全局编辑后可删除共享角色" : !character.can_manage ? "你没有删除此角色的权限" : `删除角色 ${character.name}`} disabled={character.id === "catgirl" || !sharedEditActive || !character.can_manage} onClick={() => openDeleteDialog(character)}><TrashIcon /></button>
          </>}
        </div>
      </article>)}
    </div>}

    {creatorTarget ? <CreatorTransferDialog character={creatorTarget} onClose={() => setCreatorTarget(null)} onStateReloaded={() => onCatalogChanged?.()} onTransferred={() => { const name = creatorTarget.name; setCreatorTarget(null); setNotice(`已更新角色 ${name} 的作者。`); onCatalogChanged?.(); }} /> : null}

    {createOpen ? <DialogFrame key={createStep} labelledBy="character-create-title" className="character-create" busy={createSaving || createImageValidating} initialFocus={createInitialFocus} onClose={closeCreateWizard}>
      <header><div><h3 id="character-create-title">新建角色 · {CREATE_STEPS[createStep - 1]}</h3><p>第 {createStep} / {CREATE_STEPS.length} 步</p></div><button type="button" aria-label="关闭新建角色" disabled={createSaving || createImageValidating} onClick={closeCreateWizard}>×</button></header>
      <div className="character-create-body">
      <ol className="character-create-progress" aria-label="新建角色进度">{CREATE_STEPS.map((label, index) => <li className={index + 1 === createStep ? "current" : index + 1 < createStep ? "complete" : ""} aria-current={index + 1 === createStep ? "step" : undefined} key={label}><span>{index + 1}</span>{label}</li>)}</ol>
      {createStep === 1 ? <div className="character-create-fields">
        <label className="full-width">姓名<input ref={createNameRef} type="text" maxLength={CHARACTER_NAME_MAX_LENGTH} value={createDraft.name} aria-invalid={createError.startsWith("姓名")} onChange={(event) => updateCreateDraft({ name: event.target.value })} /></label>
        <label>年龄<input className="compact-number" type="number" inputMode="numeric" min={18} max={120} value={createDraft.age} aria-describedby="character-age-hint" onChange={(event) => updateCreateDraft({ age: event.target.value })} /><small id="character-age-hint">18–120 岁，必须为成年人。</small></label>
        <label>身高<input className="compact-number" type="number" inputMode="numeric" min={80} max={250} value={createDraft.heightCm} aria-describedby="character-height-hint" onChange={(event) => updateCreateDraft({ heightCm: event.target.value })} /><small id="character-height-hint">80–250 厘米。</small></label>
        <label className="full-width">人设正文<textarea maxLength={20_000} value={createDraft.profileText} onChange={(event) => updateCreateDraft({ profileText: event.target.value })} /></label><small className="full-width character-create-count">{createDraft.profileText.length} / 20000</small>
      </div> : null}
      {createStep === 2 ? <label className="character-create-prompt">正向题词<textarea ref={createPromptRef} maxLength={4_000} value={createDraft.positivePrompt} onChange={(event) => updateCreateDraft({ positivePrompt: event.target.value })} /><small>{createDraft.positivePrompt.length} / 4000</small></label> : null}
      {createStep === 3 || createStep === 4 ? <div className="character-create-image"><CharacterImageUploadField kind={createImageKind} value={activeCreateImage} disabled={createSaving} onChange={(draft) => replaceCreateImageDraft(createImageKind, draft)} onBusyChange={setCreateImageValidating} onError={setCreateError} /></div> : null}
      {createStep === 5 && !isAdmin ? <div className="character-create-owner">
        <p className="privacy-note">你可以现在把新角色设为自己的化身，也可以创建后再通过“化身”按钮切换。</p>
        <button className={`avatar-control${createOwner ? " bound" : ""}`} type="button" aria-pressed={Boolean(createOwner)} disabled={!actorUid || createSaving} onClick={() => setCreateOwner((current) => current ? null : { uid: actorUid, name: "我", avatar_url: null })}>{createOwner ? "将作为我的化身" : "设为我的化身"}</button>
      </div> : null}
      {createStep === 5 && isAdmin ? <div className="character-create-owner">
        <p className="privacy-note">化身绑定为可选项；不选择成员即可跳过。{createOwner ? ` 当前选择：${createOwner.name}` : " 当前不绑定化身。"}</p>
        {createOwner ? <button className="secondary outlined" type="button" disabled={createSaving} onClick={() => setCreateOwner(null)}>清除成员选择</button> : null}
        <form className="member-search" onSubmit={(event) => { event.preventDefault(); loadMembers(query); }}><input ref={searchRef} type="search" maxLength={80} value={query} aria-label="搜索服务器成员" placeholder="搜索成员名称…" onChange={(event) => setQuery(event.target.value)} /><button className="primary" type="submit" disabled={membersLoading || createSaving}>搜索</button></form>
        {membersError ? <p className="action-error" role="alert">{membersError}</p> : null}
        <div className="member-list" aria-busy={membersLoading}>{membersLoading && members.length === 0 ? <SkeletonGrid items={5} variant="row" label="正在读取服务器成员" /> : !membersLoading && members.length === 0 && !membersError ? <p className="panel-state">没有匹配的成员。</p> : members.map((member) => <button className={`member-row${createOwner?.uid === member.uid ? " selected" : ""}`} type="button" key={member.uid} aria-pressed={createOwner?.uid === member.uid} disabled={createSaving} onClick={() => setCreateOwner(member)}><span className="member-avatar" aria-hidden="true">{member.avatar_url ? <img src={member.avatar_url} alt="" /> : member.name.slice(0, 1)}</span><span>{member.name}</span></button>)}</div>
        <div className="member-page-state">{membersLoading && members.length > 0 ? <Skeleton width="7rem" label="正在读取更多成员" /> : nextCursor ? <button className="secondary outlined" type="button" disabled={createSaving} onClick={() => loadMembers(query, nextCursor, true)}>加载更多</button> : members.length > 0 ? "已经到底了" : null}</div>
      </div> : null}
      {createError ? <><p className="action-error" role="alert">{createError}</p><small className="character-create-request-id">请求标识：{createRequestId}</small><button className="secondary outlined" type="button" disabled={createSaving || createExporting} onClick={() => onCatalogChanged?.()}>核对角色目录（保留草稿）</button></> : null}
      {createExportNotice ? <p className="action-success" role="status">{createExportNotice}</p> : null}
      </div>
      <footer className="character-create-actions"><button className="secondary outlined" type="button" disabled={createSaving || createExporting} onClick={closeCreateWizard}>取消</button><button className="secondary outlined" type="button" disabled={createSaving || createExporting || createImageValidating} onClick={() => void exportCreateDraft()}>{createExporting ? "导出中…" : "导出草稿"}</button>{createStep > 1 ? <button className="secondary outlined" type="button" disabled={createSaving || createExporting || createImageValidating} onClick={previousCreateStep}>上一步</button> : null}{createStep < 5 ? <button className="primary" type="button" disabled={createSaving || createExporting || createImageValidating} onClick={nextCreateStep}>下一步</button> : <button className="primary" type="button" disabled={createSaving || createExporting} onClick={() => void createCharacter()}>{createSaving ? "创建中…" : "创建角色"}</button>}</footer>
    </DialogFrame> : null}

    {deleteTarget ? <DialogFrame labelledBy="character-delete-title" className="character-delete" busy={deleteSaving} onClose={() => { if (!deleteSaving) setDeleteTarget(null); }}>
      <header><div><h3 id="character-delete-title">删除角色 · {deleteTarget.name}</h3><p>此操作会立即从当前角色菜单移除该角色。</p></div><button type="button" aria-label="关闭删除角色确认" disabled={deleteSaving} onClick={() => setDeleteTarget(null)}>×</button></header>
      <div className="character-delete-impact"><p>删除后角色将不可选，并会解除化身绑定、清理该角色的互动记录。</p><p>不会影响任何成员的个人记忆；原始素材会保留，以便之后恢复。</p></div>
      {deleteError ? <p className="action-error" role="alert">{deleteError}</p> : null}
      <div className="character-create-actions"><button className="secondary outlined" type="button" disabled={deleteSaving} onClick={() => setDeleteTarget(null)}>取消</button>{deleteError.includes("重新加载") ? <button className="secondary outlined" type="button" disabled={deleteSaving} onClick={() => void reloadDeleteConflict()}>重新加载角色列表</button> : null}<button className="danger" type="button" disabled={deleteSaving} onClick={() => void deleteCharacter()}>{deleteSaving ? "删除中…" : "确认删除"}</button></div>
    </DialogFrame> : null}

    {imageTarget ? <DialogFrame labelledBy="character-preview-title" className="character-preview" busy={imageSaving} onClose={closeImageEditor}><header><div><h3 id="character-preview-title">{imageTarget.character.name} · {imageTarget.kind === "portrait" ? "立绘" : "封图"}</h3>{adminImage ? <p>共享图片修订 {adminImage.revision}</p> : null}</div><button type="button" aria-label="关闭图片编辑" disabled={imageSaving} onClick={closeImageEditor}>×</button></header><div className={`character-image-stage ${imageTarget.kind}`}>{imageLoading ? <Skeleton variant="image" label="正在读取当前角色图片" /> : adminImage ? <button type="button" className="character-image-picker" disabled={imageSaving || imageValidating} aria-label={`选择新的${imageTarget.kind === "portrait" ? "立绘" : "封图"}`} onClick={() => imageInputRef.current?.click()}>{imageVisualLoading ? <Skeleton variant="image" label="正在载入图片预览" /> : null}<img className={imageVisualLoading ? "loading" : ""} src={imageDraft?.previewUrl ?? adminImage.url} alt={`${imageTarget.character.name}${imageTarget.kind === "portrait" ? "立绘" : "封图"}`} onLoad={() => setImageVisualLoading(false)} onError={() => { setImageVisualLoading(false); setImageError("图片预览载入失败，请刷新后重试。"); }} /></button> : null}</div><input ref={imageInputRef} hidden type="file" accept="image/png,image/jpeg,image/webp" disabled={imageSaving || imageValidating} onChange={(event) => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (file) void chooseImageFile(file); }} /><p className="character-image-hint">点击图片选择新文件。支持 PNG、JPEG、WebP，最大 10 MiB；{imageTarget.kind === "portrait" ? "立绘比例 9:21" : "封图比例 1:1"}。</p>{imageDraft ? <p className="character-image-draft">已选择 {imageDraft.width} × {imageDraft.height} 新图片，向所有用户保存后生效。</p> : null}{imageError ? <div className="character-image-error"><p className="action-error" role="alert">{imageError}</p>{imageConflict || !adminImage ? <button className="secondary outlined" type="button" disabled={imageSaving || imageLoading} onClick={() => requestImageMetadata(imageTarget)}>刷新图片</button> : null}</div> : null}<div className="profile-editor-actions"><button className="secondary outlined" type="button" disabled={imageSaving} onClick={closeImageEditor}>取消</button><button className="shared-save-button" type="button" disabled={imageSaving || imageValidating || !imageDraft || !adminImage} onClick={() => void saveImage()}>{imageSaving ? "保存中…" : imageValidating ? "校验中…" : "向所有用户保存图片"}</button></div></DialogFrame> : null}

    {bindingTarget ? <DialogFrame labelledBy="member-dialog-title" className="member" busy={busyCharacterId !== null} initialFocus={searchRef} onClose={() => setBindingTarget(null)}><header><div><h3 id="member-dialog-title">{bindingTarget.name} · 化身设置</h3><p>{bindingTarget.owner_uid ? `当前绑定：${bindingTarget.owner_name ?? "已绑定成员"}` : "当前未绑定"}</p></div><button type="button" aria-label="关闭化身设置" disabled={busyCharacterId !== null} onClick={() => setBindingTarget(null)}>×</button></header>{bindingTarget.owner_uid ? <button className="danger subtle unbind-avatar" type="button" disabled={busyCharacterId !== null} onClick={() => void updateBinding(bindingTarget, null)}>{busyCharacterId ? "解绑中…" : "解除当前绑定"}</button> : null}<form className="member-search" onSubmit={(event) => { event.preventDefault(); loadMembers(query); }}><input ref={searchRef} type="search" maxLength={80} value={query} aria-label="搜索服务器成员" placeholder="搜索成员名称…" onChange={(event) => setQuery(event.target.value)} /><button className="primary" type="submit" disabled={membersLoading || busyCharacterId !== null}>搜索</button></form>{membersError ? <p className="action-error" role="alert">{membersError}</p> : null}<div className="member-list" aria-busy={membersLoading}>{membersLoading && members.length === 0 ? <SkeletonGrid items={5} variant="row" label="正在读取服务器成员" /> : !membersLoading && members.length === 0 && !membersError ? <p className="panel-state">没有匹配的成员。</p> : members.map((member) => <button className="member-row" type="button" key={member.uid} disabled={busyCharacterId !== null} onClick={() => void updateBinding(bindingTarget, member.uid)}><span className="member-avatar" aria-hidden="true">{member.avatar_url ? <img src={member.avatar_url} alt="" /> : member.name.slice(0, 1)}</span><span>{member.name}</span></button>)}</div><div className="member-page-state">{membersLoading && members.length > 0 ? <Skeleton width="7rem" label="正在读取更多成员" /> : nextCursor ? <button className="secondary outlined" type="button" onClick={() => loadMembers(query, nextCursor, true)}>加载更多</button> : members.length > 0 ? "已经到底了" : null}</div></DialogFrame> : null}

    {profileTarget ? <DialogFrame labelledBy="profile-editor-title" className="profile-editor" busy={profileSaving} initialFocus={profileRef} onClose={closeProfile}><header><div><h3 id="profile-editor-title">共享人设 · {profileTarget.name}</h3>{profile ? <p>向所有用户生效 · 修订 {profile.profile_revision}</p> : null}</div><button type="button" aria-label="关闭人设编辑" disabled={profileSaving} onClick={closeProfile}>×</button></header>{profileLoading ? <SkeletonLines lines={8} label="正在读取当前生效人设" /> : profile ? <><label>人设全文<textarea ref={profileRef} className="profile-editor-textarea" maxLength={20_000} value={profileContent} aria-invalid={profileContent.trim().length === 0} onChange={(event) => setProfileContent(event.target.value)} /></label><small>{profileContent.length} / 20000</small>{profileError ? <p className="action-error" role="alert">{profileError}</p> : null}<div className="profile-editor-actions"><button className="secondary outlined" type="button" disabled={profileSaving} onClick={closeProfile}>取消</button><button className="shared-save-button" type="button" disabled={profileSaving || profileContent === profile.content || profileContent.trim().length === 0} onClick={() => void saveProfile()}>{profileSaving ? "保存中…" : "向所有用户保存人设"}</button></div></> : <><p className="action-error" role="alert">{profileError}</p><div className="profile-editor-actions"><button className="secondary outlined" type="button" onClick={closeProfile}>关闭</button></div></>}</DialogFrame> : null}

    {sdTarget ? <DialogFrame labelledBy="sd-editor-title" className="profile-editor" busy={sdSaving} initialFocus={positiveRef} onClose={closeSdPrompts}><header><div><h3 id="sd-editor-title">共享题词 · {sdTarget.name}</h3>{sdPrompts ? <p>向所有用户生效 · 修订 {sdPrompts.sd_revision}</p> : null}</div><button type="button" aria-label="关闭题词编辑" disabled={sdSaving} onClick={closeSdPrompts}>×</button></header>{sdLoading ? <SkeletonLines lines={5} label="正在读取当前生效题词" /> : sdPrompts ? <><label>正向题词<textarea ref={positiveRef} maxLength={4_000} value={positivePrompt} aria-invalid={positivePrompt.trim().length === 0} onChange={(event) => setPositivePrompt(event.target.value)} /></label><small>{positivePrompt.length} / 4000</small><label>负向题词<textarea maxLength={4_000} value={negativePrompt} onChange={(event) => setNegativePrompt(event.target.value)} /></label><small>{negativePrompt.length} / 4000</small>{sdError ? <p className="action-error" role="alert">{sdError}</p> : null}<div className="profile-editor-actions"><button className="secondary outlined" type="button" disabled={sdSaving} onClick={closeSdPrompts}>取消</button><button className="shared-save-button" type="button" disabled={sdSaving || (positivePrompt === sdPrompts.positive_prompt && negativePrompt === sdPrompts.negative_prompt) || positivePrompt.trim().length === 0} onClick={() => void saveSdPrompts()}>{sdSaving ? "保存中…" : "向所有用户保存题词"}</button></div></> : <><p className="action-error" role="alert">{sdError}</p><div className="profile-editor-actions"><button className="secondary outlined" type="button" onClick={closeSdPrompts}>关闭</button></div></>}</DialogFrame> : null}

  </div>;
}
